import { useState } from "react";

import { useCurrentWorkspace } from "@accurx/auth";
import { Ds, Feedback } from "@accurx/design";
import { NetworkError } from "@accurx/shared";
import { useDirectory } from "domains/directory/context/DirectoryProvider";
import {
    ContactUpdateError,
    useUpdateContact,
} from "domains/directory/hooks/mutations/useUpdateContactMutation";
import { useContactDetailsQuery } from "domains/directory/hooks/queries/useContactDetailsQuery";
import {
    ContactFormFieldErrors,
    ContactFormSubmission,
} from "domains/directory/schemas/contact-form/ContactFormSchema";
import { toast } from "react-toastify";
import styled from "styled-components";

import { DirectoryContactForm } from "../DirectoryContactForm/DirectoryContactForm";
import { DirectoryModalFooter } from "../DirectoryModal/DirectoryModalFooter";

const INVALID_EMAIL_FORM =
    "https://docs.google.com/forms/d/e/1FAIpQLSfEwXcTbLpNmyzA24Ex28b-47MHqWUB1la_gxQ6_cXfAamjQA/viewform?entry.275818450=";

type DirectoryEditContactProps = {
    contactId: string;
};

export const DirectoryEditContact = ({
    contactId,
}: DirectoryEditContactProps) => {
    const { orgId: workspaceId } = useCurrentWorkspace();

    const { setView, setSelectedContactId } = useDirectory();

    const { data, isSuccess } = useContactDetailsQuery(
        {
            workspaceId,
            contactId,
        },
        {
            onError: (error) => {
                if (error instanceof NetworkError) {
                    toast(
                        <Feedback
                            colour="error"
                            title="Couldn’t load contact. Please check your connection and try again."
                        />,
                    );
                    return;
                }
                toast(
                    <Feedback
                        colour="error"
                        title="Couldn’t load contact. Please try again."
                    />,
                );

                setView("ContactDetails");
            },
        },
    );

    const [showDiscardConfirmation, setShowDiscardConfirmation] =
        useState(false);
    const [errors, setErrors] = useState<ContactFormFieldErrors>();

    const { mutate: updateContact, isLoading: isEditingContact } =
        useUpdateContact({
            onSuccess: (data) => {
                setSelectedContactId(data.contact.id ?? undefined);
                setView("ContactDetails");
                toast(
                    <Feedback
                        colour="success"
                        title="Contact updated successfully"
                    />,
                );
            },
            onError: (error) => {
                if (error instanceof ContactUpdateError) {
                    setErrors(error.fieldErrors);
                    return;
                }
                toast(
                    <Feedback
                        colour="error"
                        title={
                            error instanceof NetworkError
                                ? "Couldn’t update contact. Please check your connection and try again."
                                : "Couldn’t update contact. Please try again."
                        }
                    />,
                );
            },
        });

    const handleSubmit = (data: ContactFormSubmission) => {
        updateContact({
            workspaceId,
            contactId,
            ...data,
        });
    };

    return (
        <>
            <DirectoryPageMainContent>
                <DirectoryPageContainer mt="3">
                    <Ds.Text
                        mb="2"
                        as="h1"
                        size="large"
                        weight="bold"
                        color="night"
                    >
                        Edit contact
                    </Ds.Text>
                    {isSuccess && !isEditingContact ? (
                        <>
                            <Ds.Text color="night">
                                {data.contact.email}
                            </Ds.Text>
                            <Ds.Link
                                target="_blank"
                                href={`${INVALID_EMAIL_FORM}${encodeURI(
                                    data.contact.email ?? "",
                                )}`}
                            >
                                Report incorrect email
                            </Ds.Link>
                            <Ds.Box my="2">
                                <hr />
                            </Ds.Box>

                            <Ds.Box my="2">
                                <Ds.Flex gap="1" alignItems="flex-start">
                                    <Ds.Icon
                                        name="Info"
                                        size="small"
                                        color="night"
                                        style={{ flexShrink: 0 }}
                                    />
                                    <Ds.Text color="night">
                                        Edits to Service and Organisation will
                                        affect all workspaces using this contact
                                    </Ds.Text>
                                </Ds.Flex>
                            </Ds.Box>

                            <DirectoryContactForm
                                id="update-contact"
                                contact={data.contact}
                                errors={errors}
                                onSubmit={handleSubmit}
                                onError={setErrors}
                            />
                        </>
                    ) : (
                        <Ds.Flex alignItems="center" justifyContent="center">
                            <Ds.Spinner />
                        </Ds.Flex>
                    )}
                </DirectoryPageContainer>
            </DirectoryPageMainContent>
            <DirectoryModalFooter>
                <DirectoryPageContainer>
                    <Ds.Flex justifyContent="space-between">
                        <Ds.Button
                            type="button"
                            appearance="tertiary"
                            onClick={() => setShowDiscardConfirmation(true)}
                        >
                            Cancel
                        </Ds.Button>
                        <Ds.Button
                            type="submit"
                            disabled={isEditingContact}
                            form="update-contact"
                        >
                            Save
                        </Ds.Button>
                    </Ds.Flex>
                </DirectoryPageContainer>
            </DirectoryModalFooter>
            <Ds.Modal
                open={showDiscardConfirmation}
                onOpenChange={setShowDiscardConfirmation}
            >
                <Ds.Modal.Section>
                    <Ds.Flex flexDirection="column" gap="2">
                        <Ds.Modal.Title>
                            Discard unsaved changes?
                        </Ds.Modal.Title>
                        <Ds.Flex gap="2" justifyContent="flex-end">
                            <Ds.Button
                                appearance="secondary"
                                onClick={() =>
                                    setShowDiscardConfirmation(false)
                                }
                            >
                                Cancel
                            </Ds.Button>
                            <Ds.Button onClick={() => setView("Search")}>
                                Discard
                            </Ds.Button>
                        </Ds.Flex>
                    </Ds.Flex>
                </Ds.Modal.Section>
            </Ds.Modal>
        </>
    );
};

const DirectoryPageContainer = styled(Ds.Box).attrs({
    mx: { xs: "2", lg: "30%" },
})``;

const DirectoryPageMainContent = styled(Ds.Flex.Item).attrs({
    flex: "1 1 0",
})`
    overflow: auto;
    padding-bottom: 1rem;
`;
